import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useParams, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";

// Context
import { LandingPageProvider } from "./context/LandingPageContext";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import { LanguageProvider } from "./context/LanguageContext";

// SEO
import SEO from "./components/SEO";

// Data
import { getVariantData } from "./data";

// Layout Components
import Header from "./components/layout/Header";
import Footer from "./components/layout/Footer";

// Long Hair FUE Promo Landing Page
import LongHairFuePage from "./pages/longhairfue/LongHairFuePage";
import StartPage from "./pages/start/StartPage";
import DionExperience from "./pages/dion/DionExperience";

// Section Components
import Hero from "./components/sections/Hero";
import BeforeAfter from "./components/sections/BeforeAfter";
import Expertise from "./components/sections/Expertise";
import Techniques from "./components/sections/Techniques";
import Process from "./components/sections/Process";
import Pricing from "./components/sections/Pricing";
import Team from "./components/sections/Team";
import AppSection from "./components/sections/AppSection";
import Clinic from "./components/sections/Clinic";
import Testimonials from "./components/sections/Testimonials";
import FAQ from "./components/sections/FAQ";
import Contact from "./components/sections/Contact";
import BeardPatterns from "./components/sections/BeardPatterns";
import BeardTechniques from "./components/sections/BeardTechniques";
import BeardProcess from "./components/sections/BeardProcess";
import EyebrowPatterns from "./components/sections/EyebrowPatterns";
import EyebrowTechniques from "./components/sections/EyebrowTechniques";
import EyebrowProcess from "./components/sections/EyebrowProcess";
import EyebrowComparison from "./components/sections/EyebrowComparison";
import HairLossPatterns from "./components/sections/HairLossPatterns";
import HairGrowthTimeline from "./components/sections/HairGrowthTimeline";
import DhiProcess from "./components/sections/DhiProcess";
import DhiMethodComparison from "./components/sections/DhiMethodComparison";
import DhiTargetAudience from "./components/sections/DhiTargetAudience";
import DhiQuickFacts from "./components/sections/DhiQuickFacts";
import DhiHybridHighlight from "./components/sections/DhiHybridHighlight";

// Modals
import HairAnalysisModal from "./components/HairAnalysisModal";
import ConsultationModal from "./components/ConsultationModal";

// Floating Components
import WhatsAppButton from "./components/WhatsAppButton";
import CookieBanner from "./components/CookieBanner";
// import HairSimPopup from "./components/HairSimPopup";
import PromoGalleryPopup from "./components/PromoGalleryPopup";

// Legal Pages
import Impressum from "./pages/Impressum";
import Datenschutz from "./pages/Datenschutz";
import AGB from "./pages/AGB";

// Blog Pages
import BlogOverview from "./pages/blog/BlogOverview";
import BlogArticle from "./pages/blog/BlogArticle";

// Admin Pages
import BlogAdmin from "./pages/admin/BlogAdmin";
import AdminHub from "./pages/admin/AdminHub";

// 404 Page
import NotFound from "./pages/NotFound";

// Price Calculator
import PriceCalculator from "./components/PriceCalculator";

// Promo Pages
import DynamicPromoPage from "./pages/DynamicPromoPage";

// Partner Pages
import PartnerLanding from "./pages/PartnerLanding";
import PartnerAuth from "./pages/PartnerAuth";
import PartnerDashboard from "./pages/PartnerDashboard";

// Partner Admin
import PartnerAdmin from "./pages/admin/PartnerAdmin";

const LandingPageContent = ({ variant }) => {
  const [isAnalysisModalOpen, setIsAnalysisModalOpen] = useState(false);
  const [isConsultationModalOpen, setIsConsultationModalOpen] = useState(false);
  const data = getVariantData(variant);
  // KI-Analyse temporär deaktiviert
  const showAIAnalysis = false;

  return (
    <LandingPageProvider data={data}>
      {/* Dynamic SEO Meta Tags */}
      <SEO variant={variant} />
      
      <div className="min-h-screen theme-bg-primary transition-colors duration-300 premium-grain relative">
        <Header 
          onOpenAnalysis={showAIAnalysis ? () => setIsAnalysisModalOpen(true) : null}
          onOpenConsultation={() => setIsConsultationModalOpen(true)}
        />
        <main>
          <Hero 
            onOpenAnalysis={showAIAnalysis ? () => setIsAnalysisModalOpen(true) : null}
            onOpenConsultation={() => setIsConsultationModalOpen(true)}
          />
          <BeforeAfter onOpenConsultation={() => setIsConsultationModalOpen(true)} />
          <Expertise />
          <BeardPatterns />
          <EyebrowPatterns />
          <EyebrowComparison />
          <DhiMethodComparison />
          <DhiQuickFacts />
          <DhiTargetAudience />
          <HairLossPatterns />
          <DhiProcess />
          <Process />
          <BeardTechniques onOpenConsultation={() => setIsConsultationModalOpen(true)} />
          <EyebrowTechniques onOpenConsultation={() => setIsConsultationModalOpen(true)} />
          <DhiHybridHighlight />
          <Techniques onOpenConsultation={() => setIsConsultationModalOpen(true)} />
          <Pricing onOpenConsultation={() => setIsConsultationModalOpen(true)} />
          <HairGrowthTimeline />
          <BeardProcess />
          <EyebrowProcess />
          <Team />
          <AppSection />
          <Clinic />
          <Testimonials onOpenConsultation={() => setIsConsultationModalOpen(true)} />
          <FAQ onOpenConsultation={() => setIsConsultationModalOpen(true)} />
          <Contact onOpenConsultation={() => setIsConsultationModalOpen(true)} />
        </main>
        <Footer />
        
        {/* Hair Analysis Modal - only show if enabled */}
        {showAIAnalysis && (
          <HairAnalysisModal 
            isOpen={isAnalysisModalOpen} 
            onClose={() => setIsAnalysisModalOpen(false)} 
          />
        )}
        
        {/* Consultation Booking Modal */}
        <ConsultationModal
          isOpen={isConsultationModalOpen}
          onClose={() => setIsConsultationModalOpen(false)}
        />

        {/* WhatsApp Floating Button */}
        <WhatsAppButton />
        
        {/* Cookie Consent Banner */}
        <CookieBanner />

        {/* Promo Gallery Popup */}
        <PromoGalleryPopup />
      </div>
    </LandingPageProvider>
  );
};

// Wrapper for route-based variants
const LandingPage = ({ variant }) => {
  return <LandingPageContent variant={variant} />;
};

// Toaster wrapper that responds to theme
const ThemedToaster = () => {
  const { isDark } = useTheme();
  return (
    <Toaster 
      position="top-right" 
      richColors 
      theme={isDark ? "dark" : "light"}
      toastOptions={{
        style: isDark ? {
          background: '#1a1a1a',
          border: '1px solid rgba(123, 167, 194, 0.2)',
        } : {
          background: '#ffffff',
          border: '1px solid rgba(90, 139, 166, 0.2)',
        },
      }}
    />
  );
};

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      return;
    }
    let frame;
    let layoutFrame;
    let cancelled = false;
    // Full document navigation also needs the final image/font layout.
    const scrollToHash = () => {
      cancelAnimationFrame(frame);
      cancelAnimationFrame(layoutFrame);
      frame = requestAnimationFrame(() => {
        layoutFrame = requestAnimationFrame(() => {
          if (cancelled) return;
          document.getElementById(hash.slice(1))?.scrollIntoView({ block: 'start', behavior: 'instant' });
        });
      });
    };
    const stopFollowing = () => { cancelled = true; };
    const events = ['wheel', 'touchstart', 'pointerdown', 'keydown'];
    scrollToHash();
    window.addEventListener('load', scrollToHash, { once: true });
    document.fonts?.ready.then(scrollToHash);
    const retry = setTimeout(scrollToHash, 150);
    events.forEach((event) => window.addEventListener(event, stopFollowing, { once: true, passive: true }));
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      cancelAnimationFrame(layoutFrame);
      clearTimeout(retry);
      window.removeEventListener('load', scrollToHash);
      events.forEach((event) => window.removeEventListener(event, stopFollowing));
    };
  }, [pathname, hash]);
  return null;
};

function App() {
  return (
    <HelmetProvider>
      <LanguageProvider>
        <ThemeProvider>
          <div className="App">
            <ThemedToaster />
            <BrowserRouter>
              <ScrollToTop />
              <Routes>
                {/* Main routes for each landing page variant */}
                <Route path="/" element={<LandingPage variant="allgemein" />} />
                <Route path="/koeln" element={<LandingPage variant="koeln" />} />
                {/* DHI page - hidden, not yet published
                <Route path="/dhi" element={<LandingPage variant="dhi" />} />
                */}
                <Route path="/bart" element={<LandingPage variant="bart" />} />
                <Route path="/augenbrauen" element={<LandingPage variant="augenbrauen" />} />
                <Route path="/longhairfue" element={<LongHairFuePage />} />
                <Route path="/start" element={<StartPage />} />
                <Route path="/experience" element={<DionExperience />} />
                
                {/* Legal pages */}
                <Route path="/impressum" element={<Impressum />} />
                <Route path="/datenschutz" element={<Datenschutz />} />
                <Route path="/agb" element={<AGB />} />
                
                {/* Blog pages */}
                <Route path="/blog" element={<BlogOverview />} />
                <Route path="/blog/:slug" element={<BlogArticle />} />
                
                {/* Admin pages */}
                <Route path="/admin" element={
                  <LanguageProvider><ThemeProvider>
                    <AdminHub />
                  </ThemeProvider></LanguageProvider>
                } />
                <Route path="/admin/blog" element={<BlogAdmin />} />
                <Route path="/admin/partners" element={
                  <LanguageProvider><ThemeProvider>
                    <PartnerAdmin />
                  </ThemeProvider></LanguageProvider>
                } />
                
                {/* Partner Pages */}
                <Route path="/partner" element={
                  <LanguageProvider><ThemeProvider>
                    <PartnerLanding />
                  </ThemeProvider></LanguageProvider>
                } />
                <Route path="/partner/register" element={
                  <LanguageProvider><ThemeProvider>
                    <PartnerAuth mode="register" />
                  </ThemeProvider></LanguageProvider>
                } />
                <Route path="/partner/login" element={
                  <LanguageProvider><ThemeProvider>
                    <PartnerAuth mode="login" />
                  </ThemeProvider></LanguageProvider>
                } />
                <Route path="/partner/dashboard" element={
                  <LanguageProvider><ThemeProvider>
                    <PartnerDashboard />
                  </ThemeProvider></LanguageProvider>
                } />

                {/* Dynamic Promo Pages */}
                <Route path="/promo/:slug" element={
                  <LanguageProvider><ThemeProvider>
                    <DynamicPromoPage />
                  </ThemeProvider></LanguageProvider>
                } />
                
                {/* Legacy Berello redirect */}
                <Route path="/berello" element={<Navigate to="/promo/berello" replace />} />
                
                {/* Price Calculator */}
                <Route path="/preisrechner" element={
                  <LanguageProvider><ThemeProvider>
                    <div className="min-h-screen theme-bg-primary transition-colors duration-300">
                      <Header />
                      <PriceCalculator />
                      <Footer />
                    </div>
                  </ThemeProvider></LanguageProvider>
                } />
                
                {/* 404 Page */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </BrowserRouter>
          </div>
        </ThemeProvider>
      </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
